import { isProduction } from 'App'
import { determineCurrentLanguageFromQueryParams, determineCurrentLanguageFromSubdomain, getLanguageToggleUrl } from 'utils/language'

/**
 * Redirects if a lang parameter in the url does not match the current page language.
 *
 * This component won't necessarily re-render properly because it relies on window.location.href.
 * This should meet our requirements however, because at the time of writing we only need this
 * check when the app initially loads
 *
 * We use the query parameter as the 'source of truth' when determining the language to redirect to
 *
 * This component is really only used in one case, and that's for when users sign in through SLS. SLS
 * redirects them to our English site with a lang parameter. This logic is built to redirect to the
 * spanish site when the user is on an english subdomain, but the lang parameter is set to spanish.
 *
 * Other logic in this app looks at both query parameter and subdomain when determining language. This
 * sounds counterintuitive but is a way that enables developers to continue testing in localhost, since
 * they can still use query parameters to set language (Chrome and other browser have security issues
 * if someone tries to access es.localhost)
 *
 * @param children
 * @returns children or null if the redirect needs to happen
 */
export const RedirectIfLangParamExistsAndIsDifferentFromSubdomain: React.FC<{}> = ({ children }) => {
  const queryParamLang = determineCurrentLanguageFromQueryParams(window.location.href)
  const url = new URL(window.location.href)
  const isLocal = url.hostname.endsWith('localhost')
  const subdomainLang = determineCurrentLanguageFromSubdomain(window.location.href)
  const subdomainMatchesPageLang = subdomainLang === queryParamLang

  if (!queryParamLang || isLocal || subdomainMatchesPageLang) {
    return <>{children}</>
  }

  window.location.assign(getLanguageToggleUrl(subdomainLang, window.location.href, isProduction))
  return null
}
